import React, { useState, useEffect } from 'react';
import { imagesAPI } from '../../api/images';
import './BlockEditor.css';

const ImageBlockEditor = ({ block, onUpdate }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await imagesAPI.getAll();
      setImages(response.images || []);
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (image) => {
    onUpdate(block.id, {
      config: {
        ...block.config,
        imageUrl: image.url,
        imageAlt: image.originalName,
        imageWidth: image.width,
        imageHeight: image.height,
      },
    });
    setShowImagePicker(false);
  };

  const handleChange = (field, value) => {
    onUpdate(block.id, {
      config: {
        ...block.config,
        [field]: value,
      },
    });
  };

  const handleStyleChange = (field, value) => {
    onUpdate(block.id, {
      styles: {
        ...block.styles,
        [field]: value,
      },
    });
  };

  return (
    <div className="block-editor">
      <div className="editor-section">
        <h4>Image Settings</h4>

        {/* Image Selection */}
        <div className="form-group">
          <label>Select Image</label>
          {block.config.imageUrl ? (
            <div className="current-image-preview">
              <img
                src={block.config.imageUrl}
                alt={block.config.imageAlt || 'Selected'}
                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
              />
              <button
                type="button"
                className="btn-secondary btn-small"
                onClick={() => setShowImagePicker(true)}
              >
                Change Image
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowImagePicker(true)}
            >
              Choose from Library
            </button>
          )}
        </div>

        {/* Image Picker Modal */}
        {showImagePicker && (
          <div className="image-picker-modal">
            <div className="modal-overlay" onClick={() => setShowImagePicker(false)} />
            <div className="modal-content">
              <div className="modal-header">
                <h3>Select Image from Library</h3>
                <button onClick={() => setShowImagePicker(false)}>✕</button>
              </div>
              <div className="modal-body">
                {loading ? (
                  <p>Loading images...</p>
                ) : images.length === 0 ? (
                  <p>No images available. Upload images first.</p>
                ) : (
                  <div className="image-grid-picker">
                    {images.map((image) => (
                      <div
                        key={image._id}
                        className="image-grid-item"
                        onClick={() => handleImageSelect(image)}
                      >
                        <img src={image.url} alt={image.originalName} />
                        <p>{image.originalName}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Alt Text */}
        <div className="form-group">
          <label>Alt Text</label>
          <input
            type="text"
            value={block.config.imageAlt || ''}
            onChange={(e) => handleChange('imageAlt', e.target.value)}
            placeholder="Image description"
          />
        </div>

        {/* Link URL */}
        <div className="form-group">
          <label>Link URL (optional)</label>
          <input
            type="url"
            value={block.config.linkUrl || ''}
            onChange={(e) => handleChange('linkUrl', e.target.value)}
            placeholder="https://example.com"
          />
        </div>

        {/* Alignment */}
        <div className="form-group">
          <label>Alignment</label>
          <select
            value={block.config.alignment || 'center'}
            onChange={(e) => handleChange('alignment', e.target.value)}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>

        {/* Max Width */}
        <div className="form-group">
          <label>Max Width (px)</label>
          <input
            type="number"
            value={block.config.maxWidth || 600}
            onChange={(e) => handleChange('maxWidth', parseInt(e.target.value))}
            min="100"
            max="600"
          />
        </div>
      </div>

      <div className="editor-section">
        <h4>Spacing</h4>

        <div className="form-group">
          <label>Padding (px)</label>
          <input
            type="number"
            value={block.styles.padding || 20}
            onChange={(e) => handleStyleChange('padding', parseInt(e.target.value))}
            min="0"
            max="100"
          />
        </div>

        <div className="form-group">
          <label>Background Color</label>
          <input
            type="color"
            value={block.styles.backgroundColor || '#ffffff'}
            onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageBlockEditor;
